/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Ref } from 'preact/hooks';
import type { ItemInfo } from './pictoChart.type';
import { calculateOffset, useDatatip } from '../hooks/PRIVATE_useDatatip';
import { ComponentChildren } from 'preact';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Property } from 'csstype';

type Props = {
  touchResponse: 'touchStart' | 'touchHold';
  content?: ComponentChildren;
  borderColor?: Property.BorderColor;
  focusedItemInfo: ItemInfo;
  width: number;
  focusedItemRef: Ref<HTMLDivElement>;
};

/**
 * Returns the datatip.
 * @param content The text string for the picto chart item.
 * @param focusedItemRef The ref for focused item node.
 * @param focusedItemInfo The ItemInfo for focused item node.
 * @returns
 */
export const usePictoChartDatatip = ({
  touchResponse,
  content,
  borderColor,
  width,
  focusedItemRef,
  focusedItemInfo
}: Props) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';

  const itemDim = focusedItemRef.current?.getBoundingClientRect();
  const { datatipContent, datatipProps } = useDatatip({
    content,
    placement: 'top-start',
    offset: calculateOffset(isRtl, width, focusedItemInfo.isCurrent ? itemDim : undefined),
    borderColor,
    anchor: focusedItemInfo?.isCurrent ? 'element' : 'pointer',
    touchResponse
  });
  return { datatipContent: datatipContent, datatipProps: datatipProps };
};
